import { FormikConfig, FormikHelpers, FormikProps, useFormik } from 'formik';
import { compact, debounce, isUndefined, pickBy } from 'lodash';
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from "react-redux";
import { Nullable } from "../../../../types/utils";
import * as Api from '../../../api';
import { PresetObj } from '../../../api-entities/PresetArea';
import { TApiArea } from '../../../api/endpoints/Area';
import { TApiSubset } from '../../../api/endpoints/Subset';
import { TApiSubsetOptions } from '../../../api/endpoints/SubsetOption';
import { Area } from '../../../entities/Area';
import { selectParameters } from "../../../store/selectors/new/parameters";
import { snapAreaToGrid } from "../../../utils/Area";
import DatasetProviderLogo from "../.././DatasetProviderLogo";
import BeforeUnload from '../../Feedback/BeforeUnload';
import Footnote from '../Footnote';
import FormError from '../FormError';
import { TParamLevelistValue } from '../ParamLevelistSelector';
import { TParamQuantileValue } from '../ParamsQuantileSelector';
import RunsSelector from '../RunsSelector';
import { TTimeStepValue } from '../TimeStepSelector';
import { TUseStepValue } from '../UseStepSelector';
import AreasField from "./controls/AreasField";
import DirectionField from "./controls/DirectionField";
import EnsembleMembersField, { deriveNumberValuesFromSubsetOption } from "./controls/EnsembleMembersField";
import ForecastMonthsField from "./controls/ForecastMonthsField";
import ForecastStepsField from "./controls/ForecastStepsField";
import ForecastTimeStepsField from "./controls/ForecastTimeStepsField";
import ForecastUseStepsField from "./controls/ForecastUseStepsField";
import FrequencyField from "./controls/FrequencyField";
import GridField from "./controls/GridField";
import HindcastField from "./controls/HindcastField";
import LevelistField from "./controls/LevelistField";
import LocationField from "./controls/LocationField";
import NameField from "./controls/NameField";
import ParametersField from "./controls/ParametersField";
import ParametersLevelistField from "./controls/ParametersLevelistField";
import ParametersQuantileField from "./controls/ParametersQuantileField";
import SatelliteChannelsField from "./controls/SatelliteChannelsField";
import SubsetInfo from "./controls/SubsetInfo";
import TimeField from "./controls/TimeField";
import { validateAreas, validateLocation, validateProductArrayProperty } from './validators';

const areaToPresetObj = (area: TApiArea): PresetObj => {
  return {
    name: area.name,
    area: {
      north: parseFloat(area.north),
      west: parseFloat(area.west),
      south: parseFloat(area.south),
      east: parseFloat(area.east)
    }
  };
};


export interface IProductFormValues {
  name: string;
  hindcast: Nullable<Array<string>>;
  steps: Array<string>;
  number: Array<number>;
  areas: Array<Area>;
  grid: Array<string>;
  use: Array<string>;
  fcmonth: Array<number>;
  param: Array<string>;
  levelist: Array<string>;
  param_levelist: Array<TParamLevelistValue>;
  param_quantile: Array<TParamQuantileValue>;
  time: Array<string>;
  time_step: Array<TTimeStepValue>;
  use_step: Array<TUseStepValue>;
  direction: Array<string>;
  frequency: Array<string>;
  location: Nullable<number>;
  channel: Array<number>;
}

export type FormikProductFormProps = FormikConfig<IProductFormValues>;
export type FormikProductFormField = {
  formik: FormikProps<IProductFormValues>;
}

export const DEFAULT_PRODUCT_FORM_VALUES: Readonly<IProductFormValues> = {
  name: '',
  hindcast: null,
  steps: [],
  number: [],
  areas: [],
  grid: [],
  use: [],
  fcmonth: [],
  param: [],
  levelist: [],
  param_levelist: [],
  param_quantile: [],
  time: [],
  time_step: [],
  use_step: [],
  location: null,
  channel: [],
  frequency: [],
  direction: [],
}


const getDefaultEnsembleMembers = (subsetOptions: TApiSubsetOptions) => {
  const { number } = subsetOptions;

  if (!number) {
    return [];
  }


  return deriveNumberValuesFromSubsetOption(number).filter(v => v <= 100);
}

const getDefaultGrid = (subsetOptions: TApiSubsetOptions) => {
  const { grid } = subsetOptions;

  if (!grid) {
    return [];
  }

  if ('legs' in grid.value) {
    return grid.value.legs.map(leg => leg[0]);
  }

  return [grid.value.grid[0]];
}

const computeMinGrid = (grids: IProductFormValues['grid']) => {
  return Math.min(...compact(grids.map(parseFloat)));
}

const transformAreasToMatchGrids = (areas: IProductFormValues['areas'], grids: IProductFormValues['grid']) => {
  const minGrid = computeMinGrid(grids);

  return isFinite(minGrid) ? areas.map(area => snapAreaToGrid(area, minGrid)) : areas;
}

export type TFormErrorMessage = string | undefined;

export type TProductFormErrors = {
  [key in keyof IProductFormValues]?: TFormErrorMessage
}
const validateProductFormValues = (
  subsetOptions: TApiSubsetOptions,
  includeLocation: boolean | void,
  hindcastSelected: boolean
) => (values: IProductFormValues): TProductFormErrors => {
  const errors: TProductFormErrors = {};

  errors.steps = subsetOptions.step && validateProductArrayProperty(values.steps);
  errors.number = subsetOptions.number && validateProductArrayProperty(values.number);
  errors.areas = subsetOptions.area && validateAreas(values.areas);
  errors.grid = subsetOptions.grid && validateProductArrayProperty(values.grid);
  errors.use = subsetOptions.use && validateProductArrayProperty(values.use);
  errors.hindcast = (subsetOptions.hindcast && hindcastSelected) ? validateProductArrayProperty(values.hindcast) : undefined;
  errors.fcmonth = subsetOptions.fcmonth && validateProductArrayProperty(values.fcmonth);
  errors.param = subsetOptions.param && validateProductArrayProperty(values.param);
  errors.levelist = subsetOptions.levelist && validateProductArrayProperty(values.levelist);
  errors.param_levelist = subsetOptions.param_levelist && validateProductArrayProperty(values.param_levelist);
  errors.param_quantile = subsetOptions.param_quantile && validateProductArrayProperty(values.param_quantile);
  errors.time = subsetOptions.time && validateProductArrayProperty(values.time);
  errors.time_step = subsetOptions.time_step && validateProductArrayProperty(values.time_step);
  errors.use_step = subsetOptions.use_step && validateProductArrayProperty(values.use_step);
  errors.location = includeLocation ? validateLocation(values.location) : undefined;
  errors.channel = subsetOptions.channel && validateProductArrayProperty(values.channel);
  errors.direction = subsetOptions.direction && validateProductArrayProperty(values.direction);
  errors.frequency = subsetOptions.frequency && validateProductArrayProperty(values.frequency);

  return pickBy(errors, v => !isUndefined(v));
}

const ProductForm = (props: {
  subset: TApiSubset;
  initialValues?: Partial<IProductFormValues>;
  subsetOptions: TApiSubsetOptions;
  actions?: (
    subset: TApiSubset,
    subsetOptions: TApiSubsetOptions,
    formik: FormikProps<IProductFormValues>
  ) => ReactNode;
  onSubmit: (values: IProductFormValues, formik: FormikHelpers<IProductFormValues>) => void;
  onChange?: (values: IProductFormValues) => void;
  includeLocation?: boolean;
}) => {
  const {
    subsetOptions,
    initialValues,
    includeLocation,
    subset,
    actions,
    onChange,
    onSubmit
  } = props;
  const [presetAreas, setPresetAreas] = useState<Array<PresetObj>>([]);
  const [userPresetAreas, setUserPresetAreas] = useState<Array<PresetObj>>([]);
  const [hindcastSelected, setHindcastSelected] = useState<boolean>(Boolean(initialValues?.hindcast?.length));

  const availableParams = useSelector(selectParameters);

  const formik = useFormik<IProductFormValues>({
    initialValues: {
      ...DEFAULT_PRODUCT_FORM_VALUES,
      number: getDefaultEnsembleMembers(subsetOptions),
      grid: getDefaultGrid(subsetOptions),
      ...initialValues
    },
    validate: validateProductFormValues(subsetOptions, includeLocation, hindcastSelected),
    onSubmit
  });

  const onAreasChange = useCallback((values: IProductFormValues['areas']) => {
    const { grid } = formik.values;

    formik.setFieldValue('areas', transformAreasToMatchGrids(values, grid));
  }, [formik.values.areas])

  useEffect(() => {
    Api.Area.list().then(areas => {
      setPresetAreas(areas.map(areaToPresetObj));
    });

    Api.UserArea.list().then(areas => {
      setUserPresetAreas(areas.map(areaToPresetObj));
    });
  }, []);

  useLayoutEffect(() => {
    if (Object.keys(formik.errors).length !== 0) {
      document.querySelector('.form-error')?.closest('.form-group')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [formik.submitCount]);

  useEffect(debounce(() => {
    onChange && onChange(formik.values);
  }, 16), [formik.values]);

  useEffect(() => {
    const { areas, grid } = formik.values;

    formik.setFieldValue('areas', transformAreasToMatchGrids(areas, grid));
  }, [formik.values.grid]);

  useEffect(() => {
    formik.setFieldValue('hindcast', hindcastSelected ? formik.values.hindcast : null);
  }, [hindcastSelected]);

  return (
    <BeforeUnload enabled={formik.dirty && !formik.isSubmitting}>
      <Form onSubmit={formik.handleSubmit} className="product-form">
        <div className="heading">
          <SubsetInfo subset={subset} />
          <div className="provider">
            <DatasetProviderLogo provider={subset.provider} />
          </div>
        </div>

        <NameField formik={formik} />

        {
          subsetOptions.time &&
          <TimeField formik={formik} spec={subsetOptions.time} />
        }

        {
          subsetOptions.step &&
          <ForecastStepsField spec={subsetOptions.step} formik={formik} />
        }

        {
          subsetOptions.time_step &&
          <ForecastTimeStepsField spec={subsetOptions.time_step} formik={formik} />
        }

        {
          subsetOptions.use_step &&
          <ForecastUseStepsField spec={subsetOptions.use_step} formik={formik} />
        }

        {
          subsetOptions.number &&
          <EnsembleMembersField spec={subsetOptions.number} formik={formik} />
        }

        {
          subsetOptions.area &&
          <AreasField
            spec={subsetOptions.area}
            presetAreas={presetAreas}
            userPresetAreas={userPresetAreas}
            onChange={onAreasChange}
            formik={formik}
          />
        }

        {
          subsetOptions.grid &&
          <GridField spec={subsetOptions.grid} formik={formik} />
        }

        {
          includeLocation &&
          <LocationField formik={formik} />
        }

        {
          subsetOptions.use &&
          <Form.Group controlId="use">
            <Form.Label>Runs</Form.Label>
            <RunsSelector
              values={formik.values.use}
              spec={subsetOptions.use}
              onChange={values => formik.setFieldValue('use', values)}
            />

            <FormError
              message={formik.errors.use as TFormErrorMessage}
              when={formik.touched.use && Boolean(formik.errors.use)}
            />

            <Footnote text={subsetOptions.use.footnote} />
          </Form.Group>
        }

        {
          subsetOptions.hindcast &&
          <HindcastField
            spec={subsetOptions.hindcast}
            isSelected={hindcastSelected}
            onToggle={setHindcastSelected}
            formik={formik}
          />
        }

        {
          subsetOptions.fcmonth &&
          <ForecastMonthsField spec={subsetOptions.fcmonth} formik={formik} />
        }

        {
          subsetOptions.channel &&
          <SatelliteChannelsField spec={subsetOptions.channel} formik={formik} />
        }

        {
          subsetOptions.direction &&
          <DirectionField spec={subsetOptions.direction} formik={formik} />
        }

        {
          subsetOptions.frequency &&
          <FrequencyField spec={subsetOptions.frequency} formik={formik} />
        }

        {
          subsetOptions.param &&
          <ParametersField spec={subsetOptions.param} formik={formik} />
        }

        {
          subsetOptions.levelist &&
          <LevelistField spec={subsetOptions.levelist} formik={formik} />
        }

        {
          subsetOptions.param_levelist &&
          <ParametersLevelistField spec={subsetOptions.param_levelist} options={availableParams} formik={formik} />
        }

        {
          subsetOptions.param_quantile &&
          <ParametersQuantileField spec={subsetOptions.param_quantile} options={availableParams} formik={formik} />
        }

        <div className="actions">
          {
            actions ?
              actions(subset, subsetOptions, formik)
              : <Button type="submit" disabled={formik.isSubmitting}>
                Create
              </Button>
          }
        </div>
      </Form>
    </BeforeUnload>
  );
};

export default ProductForm;
